/**
 * Demo business-system integration — user-side surface.
 *
 * Part of openspec change `demo-integration-and-channel-selectable-topup`
 * (design D7). This page is the *operator-visible* half of the reference
 * integration: it drives a real wallet top-up, a real payment and a real
 * refund, and shows the receiver's decisions next to the resulting ledger
 * movement, so "the contract works" is something you watch rather than infer
 * from server logs.
 *
 * Everything rendered here comes from live API calls. There is no seeded or
 * illustrative data anywhere in this file — an empty panel means the system
 * genuinely has nothing to show.
 */

import { useCallback, useEffect, useState } from 'react'
import { api, ApiRequestError } from '@/lib/api'

interface DecisionLogEntry {
  received_at?: number
  outcome?: string
  reason?: string
  event_id?: string
  order_id?: string
  status?: string
  event_type?: string
  signature_verified?: boolean
  idempotency?: string
  fulfilment?: string
  business_order_id_present?: boolean
}

interface LedgerEntry {
  id?: string
  entry_type?: string
  direction?: string
  amount?: number
  currency?: string
  status?: string
  created_at?: string
}

interface BalanceBucket {
  currency?: string
  balance?: number
  sub_account_type?: string
}

const DEMO_LOG_PATH = '/api/v1/demo/log'
const API_BASE = '/api/v1'

/**
 * Credentials an integrator types in to test as their own application.
 *
 * Deliberately held in component state only — never localStorage, never a
 * cookie, never sent anywhere but this platform's own API. A test console
 * that quietly persists someone else's app secret is a credential leak
 * waiting to happen, so closing the tab must be enough to forget it.
 */
interface AppCredentials {
  appKey: string
  appSecret: string
}

/** Call the platform API as the *integrator's* application, not as the operator. */
async function callAsApp<T>(
  creds: AppCredentials,
  method: string,
  path: string,
  body?: unknown
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-App-Key': creds.appKey,
      'X-App-Secret': creds.appSecret,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  const text = await res.text()
  let parsed: unknown = null
  try {
    parsed = text ? JSON.parse(text) : null
  } catch {
    parsed = text
  }
  if (!res.ok) {
    const err = (parsed as { error?: { code?: string; message?: string } | string })?.error
    const message = typeof err === 'string' ? err : err?.message ?? `HTTP ${res.status}`
    throw new ApiRequestError(
      { code: typeof err === 'string' ? 'error' : err?.code ?? 'unknown', message },
      res.status
    )
  }
  const withData = parsed as { data?: T }
  return (withData && typeof withData === 'object' && 'data' in withData
    ? (withData.data as T)
    : (parsed as T))
}

function formatTime(epochSeconds?: number): string {
  if (!epochSeconds) return '—'
  return new Date(epochSeconds * 1000).toLocaleTimeString()
}

function outcomeTone(outcome?: string): string {
  if (outcome === 'accepted') return 'text-emerald-700 bg-emerald-50 border-emerald-200'
  if (outcome === 'duplicate') return 'text-amber-700 bg-amber-50 border-amber-200'
  return 'text-red-700 bg-red-50 border-red-200'
}

export default function DemoIntegrationPage() {
  const [appKey, setAppKey] = useState('')
  const [appSecret, setAppSecret] = useState('')
  const [channels, setChannels] = useState<string[]>([])
  const [webhookSecret, setWebhookSecret] = useState('')
  const [secretFingerprint, setSecretFingerprint] = useState<string | null>(null)
  const [accountId, setAccountId] = useState('')
  const [channelType, setChannelType] = useState('paykka')
  const [amount, setAmount] = useState('100')
  const [currency, setCurrency] = useState('USD')

  const [balance, setBalance] = useState<BalanceBucket[]>([])
  const [ledger, setLedger] = useState<LedgerEntry[]>([])
  const [log, setLog] = useState<DecisionLogEntry[]>([])
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null)
  const [lastOrderId, setLastOrderId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  /** The receiver lives outside /api/v1, so it is fetched directly. */
  const refreshLog = useCallback(async () => {
    try {
      const res = await fetch(`${DEMO_LOG_PATH}?limit=25`)
      if (!res.ok) return
      const body = await res.json()
      setLog(Array.isArray(body.entries) ? body.entries : [])
    } catch {
      /* the log is evidence, not a dependency — never break the page for it */
    }
  }, [])

  const refreshAccount = useCallback(async () => {
    if (!accountId.trim()) return
    try {
      const creds = { appKey, appSecret }
      const [balanceRes, ledgerRes] = await Promise.all([
        callAsApp<{ balances?: BalanceBucket[] }>(creds, 'GET', `/accounts/${accountId}/balance`),
        callAsApp<{ entries?: LedgerEntry[] }>(creds, 'GET', `/accounts/${accountId}/ledger?limit=20`),
      ])
      setBalance(Array.isArray(balanceRes?.balances) ? balanceRes.balances : [])
      setLedger(Array.isArray(ledgerRes?.entries) ? ledgerRes.entries : [])
      setError(null)
    } catch (err: unknown) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Failed to load account')
    }
  }, [accountId, appKey, appSecret])

  useEffect(() => {
    refreshLog()
    const timer = setInterval(refreshLog, 5000)
    return () => clearInterval(timer)
  }, [refreshLog])

  /** Doubles as the credential smoke test: it is the first authenticated call. */
  const loadChannels = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await callAsApp<{ channels?: { channel_type?: string }[] }>(
        { appKey, appSecret },
        'GET',
        '/payments/channels'
      )
      const names = (res?.channels ?? [])
        .map((c) => c.channel_type)
        .filter((c): c is string => Boolean(c))
      setChannels(names)
      if (names.length > 0 && !names.includes(channelType)) setChannelType(names[0])
    } catch (err: unknown) {
      setError(
        err instanceof ApiRequestError
          ? `${err.error.message} (HTTP ${err.status})`
          : 'Failed to list channels'
      )
    } finally {
      setBusy(false)
    }
  }

  /**
   * Hand the receiver the secret it should verify with.
   *
   * This is the integrator doing what the guidance's §1.1/§1.2 describe:
   * take the application's effective webhook secret (`payment_webhook_secret`
   * if set, else `app_secret`) and put it into the receiver's configuration.
   * The value goes one way — the server answers with a fingerprint only.
   */
  const configureReceiverSecret = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await api.put<{ secret_fingerprint?: string }>('/demo/secret', {
        secret: webhookSecret,
      })
      setSecretFingerprint(res.data?.secret_fingerprint ?? null)
      setWebhookSecret('')
    } catch (err: unknown) {
      setError(
        err instanceof ApiRequestError ? err.error.message : 'Could not configure the receiver'
      )
    } finally {
      setBusy(false)
    }
  }

  const startTopup = async () => {
    setBusy(true)
    setError(null)
    setCheckoutUrl(null)
    try {
      const res = await callAsApp<{ checkout_url?: string; payment_order_id?: string }>(
        { appKey, appSecret },
        'POST',
        `/accounts/${accountId}/topup`,
        {
          amount: Number(amount),
          currency,
          channel_type: channelType,
          success_url: `${window.location.origin}/demo?topup=done`,
          cancel_url: `${window.location.origin}/demo?topup=cancelled`,
        }
      )
      setCheckoutUrl(res?.checkout_url ?? null)
      setLastOrderId(res?.payment_order_id ?? null)
    } catch (err: unknown) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Top-up failed')
    } finally {
      setBusy(false)
    }
  }

  const refundLastOrder = async () => {
    if (!lastOrderId) return
    setBusy(true)
    setError(null)
    try {
      await callAsApp({ appKey, appSecret }, 'POST', `/payments/${lastOrderId}/refund`, {})
      await refreshAccount()
      await refreshLog()
    } catch (err: unknown) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Refund failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Demo Integration</h1>
        <p className="text-sm text-muted-foreground">
          A reference business system wired to this platform exactly as{' '}
          <code className="text-xs">docs/payment-notification-guidance.md</code> instructs:
          it verifies signatures with its own configured secret, consumes only{' '}
          <code className="text-xs">data.object</code>, and dedupes on{' '}
          <code className="text-xs">event.id</code>. It is a test module and never
          serves real tenant traffic.
        </p>
      </header>

      {error && (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <section aria-labelledby="creds-heading" className="rounded-lg border p-5">
        <h2 id="creds-heading" className="mb-1 text-lg font-medium">
          Your application credentials
        </h2>
        <p className="mb-4 text-xs text-muted-foreground">
          Held in this tab only — never stored, never sent anywhere but this platform's
          own API. Close the tab and they are gone. These are the same
          <code className="mx-1">X-App-Key</code>/<code className="mx-1">X-App-Secret</code>
          headers your server-side integration sends.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">X-App-Key</span>
            <input
              className="w-full rounded-md border px-3 py-2 font-mono text-xs"
              value={appKey}
              onChange={(e) => setAppKey(e.target.value)}
              placeholder="app key"
              autoComplete="off"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">X-App-Secret</span>
            <input
              className="w-full rounded-md border px-3 py-2 font-mono text-xs"
              value={appSecret}
              onChange={(e) => setAppSecret(e.target.value)}
              placeholder="app secret"
              type="password"
              autoComplete="off"
            />
          </label>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            className="rounded-md border px-4 py-2 text-sm font-medium transition-colors hover:bg-slate-50 disabled:opacity-50"
            onClick={loadChannels}
            disabled={busy || !appKey.trim() || !appSecret.trim()}
          >
            Test credentials &amp; list channels
          </button>
          {channels.length > 0 && (
            <span className="text-sm text-emerald-700">
              {channels.length} channels: {channels.join(', ')}
            </span>
          )}
        </div>
      </section>

      <section aria-labelledby="controls-heading" className="rounded-lg border p-5">
        <h2 id="controls-heading" className="mb-4 text-lg font-medium">
          Drive the flow
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Account ID</span>
            <input
              className="w-full rounded-md border px-3 py-2"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              onBlur={refreshAccount}
              placeholder="account uuid"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Channel</span>
            <select
              className="w-full rounded-md border px-3 py-2"
              value={channelType}
              onChange={(e) => setChannelType(e.target.value)}
            >
              {(channels.length > 0 ? channels : ['paykka', 'stripe_checkout']).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Amount (minor units)</span>
            <input
              className="w-full rounded-md border px-3 py-2"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="numeric"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Currency</span>
            <input
              className="w-full rounded-md border px-3 py-2"
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            />
          </label>
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          <button
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700 disabled:opacity-50"
            onClick={startTopup}
            disabled={busy || !accountId.trim() || !appKey.trim() || !appSecret.trim()}
          >
            Fund wallet
          </button>
          <button
            className="rounded-md border px-4 py-2 text-sm font-medium transition-colors hover:bg-slate-50 disabled:opacity-50"
            onClick={refreshAccount}
            disabled={busy || !accountId.trim()}
          >
            Refresh wallet
          </button>
          <button
            className="rounded-md border px-4 py-2 text-sm font-medium transition-colors hover:bg-slate-50 disabled:opacity-50"
            onClick={refundLastOrder}
            disabled={busy || !lastOrderId}
          >
            Refund last order
          </button>
        </div>

        {checkoutUrl && (
          <p className="mt-4 text-sm">
            Hosted checkout ready —{' '}
            <a className="font-medium underline" href={checkoutUrl} target="_blank" rel="noreferrer">
              pay as the user
            </a>
            {lastOrderId && <span className="text-muted-foreground"> (order {lastOrderId})</span>}
          </p>
        )}
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section aria-labelledby="receiver-config-heading" className="rounded-lg border p-5">
        <h2 id="receiver-config-heading" className="mb-1 text-lg font-medium">
          Receiver verification secret
        </h2>
        <p className="mb-4 text-xs text-muted-foreground">
          The demo receiver verifies signatures with a secret you give it — it never
          reads your application's stored credentials. Use the application's effective
          webhook secret: <code>payment_webhook_secret</code> if you configured one,
          otherwise the <code>app_secret</code>. Sent one way; the server answers with a
          fingerprint, never the value.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Webhook secret</span>
            <input
              className="w-80 rounded-md border px-3 py-2 font-mono text-xs"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              type="password"
              autoComplete="off"
              placeholder="effective webhook secret"
            />
          </label>
          <button
            className="rounded-md border px-4 py-2 text-sm font-medium transition-colors hover:bg-slate-50 disabled:opacity-50"
            onClick={configureReceiverSecret}
            disabled={busy || !webhookSecret.trim()}
          >
            Configure receiver
          </button>
          {secretFingerprint && (
            <span className="text-sm text-emerald-700">
              configured · fingerprint <code>{secretFingerprint}</code>
            </span>
          )}
        </div>
      </section>

      <section aria-labelledby="wallet-heading" className="rounded-lg border p-5">
          <h2 id="wallet-heading" className="mb-4 text-lg font-medium">
            Wallet
          </h2>
          {balance.length === 0 ? (
            <p className="text-sm text-muted-foreground">No balance loaded.</p>
          ) : (
            <ul className="mb-4 space-y-1">
              {balance.map((b, i) => (
                <li key={i} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {b.sub_account_type ?? 'cash'} · {b.currency}
                  </span>
                  <span className="font-mono font-medium">{b.balance}</span>
                </li>
              ))}
            </ul>
          )}

          <h3 className="mb-2 text-sm font-medium text-muted-foreground">Ledger</h3>
          {ledger.length === 0 ? (
            <p className="text-sm text-muted-foreground">No ledger entries.</p>
          ) : (
            <ul className="space-y-1">
              {ledger.map((e, i) => (
                <li key={e.id ?? i} className="flex justify-between gap-2 text-sm">
                  <span>
                    <span className="font-medium">{e.entry_type}</span>{' '}
                    <span className="text-muted-foreground">{e.direction}</span>
                  </span>
                  <span className="font-mono">{e.amount}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section aria-labelledby="receiver-heading" className="rounded-lg border p-5">
          <h2 id="receiver-heading" className="mb-1 text-lg font-medium">
            Receiver decisions
          </h2>
          <p className="mb-4 text-xs text-muted-foreground">
            Redis-backed with a TTL — evidence for the run in progress, not durable history.
          </p>
          {log.length === 0 ? (
            <p className="text-sm text-muted-foreground">No deliveries received yet.</p>
          ) : (
            <ul className="space-y-2">
              {log.map((entry, i) => (
                <li key={`${entry.event_id ?? 'x'}-${i}`} className={`rounded-md border px-3 py-2 text-sm ${outcomeTone(entry.outcome)}`}>
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">{entry.outcome ?? 'unknown'}</span>
                    <span className="font-mono text-xs">{formatTime(entry.received_at)}</span>
                  </div>
                  <div className="mt-1 text-xs">
                    {entry.reason ? (
                      <span>{entry.reason}</span>
                    ) : (
                      <span>
                        {entry.event_type} · order {entry.order_id} · {entry.idempotency}
                        {entry.business_order_id_present === false && ' · no theRouterOrderId'}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  )
}
